// public-frontend/src/utils/googleSignIn.ts
/**
 * GOOGLE SIGN-IN PROMPT GUARD
 * ──────────────────────────────────────────────────────────────────────────
 * This app uses its own styled button and calls `google.accounts.id.prompt()`
 * rather than rendering Google's iframe button. `prompt()` is fire-and-forget:
 * when the page origin is not listed under "Authorized JavaScript origins" for
 * the OAuth client id, or the browser blocks third-party cookies, NOTHING
 * happens — no dialog, no exception, no user-visible feedback. The only trace
 * is a console line:
 *
 *   [GSI_LOGGER]: The given origin is not allowed for the given client ID.
 *
 * See Production-Issues/user-cannot-login/what-fixed.md §"Still open".
 *
 * `prompt()` accepts a moment-notification listener that reports why the UI did
 * not display (`unregistered_origin`, `opt_out_or_no_session`, …). Google's
 * FedCM migration deprecates parts of that API, so every accessor is called
 * defensively — if the notification shape is missing we fall back to a timer
 * and treat "nothing happened" as failure.
 *
 * This does NOT fix an unregistered origin — nothing client-side can. It turns
 * a dead button into a diagnosable, explained one.
 */

import { log } from './logger';

const COMPONENT = 'GoogleSignIn';

/** If no Google UI and no moment notification by now, assume it never opened. */
const PROMPT_GRACE_MS = 2500;

/**
 * Trigger Google one-tap / sign-in and report failure to display.
 *
 * @param onUnavailable called (at most once) with a reason when no Google UI
 *                      appeared — wire it to visible UI, never swallow it.
 */
export function promptGoogleSignIn(onUnavailable: (reason: string) => void): void {
  const google = (window as any).google;
  if (!google?.accounts?.id) {
    onUnavailable('script-not-loaded');
    return;
  }

  let settled = false;
  const fail = (reason: string) => {
    if (settled) return;
    settled = true;
    log.warn(COMPONENT, '⚠️ Google sign-in did not open', {
      reason,
      origin: window.location.origin,
      clientIdTail: String(import.meta.env.VITE_GOOGLE_CLIENT_ID || '').slice(-24),
      fix: 'If reason is unregistered_origin, add this exact origin to Authorized JavaScript origins for this OAuth client id in Google Cloud Console',
    });
    onUnavailable(reason);
  };

  const timer = window.setTimeout(() => fail('no-response'), PROMPT_GRACE_MS);

  try {
    // The listener is optional and its accessors are FedCM-dependent — guard all.
    google.accounts.id.prompt((notification: any) => {
      const notDisplayed =
        typeof notification?.isNotDisplayed === 'function' && notification.isNotDisplayed();
      const skipped =
        typeof notification?.isSkippedMoment === 'function' && notification.isSkippedMoment();

      if (notDisplayed) {
        const reason =
          (typeof notification?.getNotDisplayedReason === 'function' &&
            notification.getNotDisplayedReason()) ||
          'not-displayed';
        window.clearTimeout(timer);
        fail(String(reason));
        return;
      }

      if (skipped) {
        // User dismissed it — that is not a failure, just stop the fallback timer.
        window.clearTimeout(timer);
        settled = true;
        return;
      }

      // Displayed. Cancel the pessimistic timer.
      window.clearTimeout(timer);
      settled = true;
    });
  } catch (err) {
    window.clearTimeout(timer);
    log.error(COMPONENT, 'prompt() threw', { err: String(err), origin: window.location.origin });
    fail('prompt-threw');
  }
}

/** User-facing copy for a Google sign-in that never opened. */
export function googleUnavailableMessage(reason: string): string {
  if (reason === 'opt_out_or_no_session') {
    return 'No Google session found in this browser. Sign in to Google first, or use your email and password.';
  }
  if (reason === 'browser_not_supported' || reason === 'no-response') {
    return 'Google sign-in is blocked in this browser (often third-party cookies). Please sign in with your email and password.';
  }
  return 'Google sign-in is unavailable on this site right now. Please sign in with your email and password.';
}
